export type MoveInConditionItem = {
  id: string;
  label: string;
};

export type MoveInConditionFormKind = "taa" | "naa" | "nc" | "sc" | "tn";

export type MoveInConditionSection = {
  id: string;
  title: string;
  descriptionLabel?: string;
  items: MoveInConditionItem[];
};

type MoveInConditionSchema = {
  acknowledgmentText: string[];
  introText: string;
  sections: MoveInConditionSection[];
  safetyItems: MoveInConditionItem[];
  safetyTitle: string;
  showPestConcerns: boolean;
  showSpecialProvisions: boolean;
};

const isStateMoveInConditionForm = (formKind: MoveInConditionFormKind) =>
  formKind === "nc" || formKind === "sc" || formKind === "tn";

export const getMoveInConditionFormKind = (
  state?: string | null
): MoveInConditionFormKind => {
  const normalizedState = state?.trim().toUpperCase();
  if (normalizedState === "TX" || normalizedState === "TEXAS") return "taa";
  if (normalizedState === "NC" || normalizedState === "NORTH CAROLINA")
    return "nc";
  if (normalizedState === "SC" || normalizedState === "SOUTH CAROLINA")
    return "sc";
  if (normalizedState === "TN" || normalizedState === "TENNESSEE") return "tn";
  return "naa";
};

const waterStainLabel = (formKind: MoveInConditionFormKind) =>
  formKind === "taa"
    ? "Water stains or mold on walls, ceilings or baseboards"
    : "Water stains on walls or ceilings";

const plumbingWaterStainLabel = (formKind: MoveInConditionFormKind) =>
  formKind === "taa"
    ? "Plumbing leaks, water stains or mold on walls, ceilings or baseboards"
    : "Plumbing leaks or water stains on walls or ceilings";

const roomItems = (
  formKind: MoveInConditionFormKind
): MoveInConditionItem[] => [
  { id: "walls", label: "Walls" },
  { id: "wallpaper", label: "Wallpaper" },
  { id: "plugs_switches_ac_vents", label: "Plugs, switches, A/C vents" },
  { id: "woodwork_baseboards", label: "Woodwork/baseboards" },
  { id: "ceiling", label: "Ceiling" },
  { id: "light_fixtures_bulbs", label: "Light fixtures, bulbs" },
  { id: "floor_carpet", label: "Floor/carpet" },
  { id: "doors_stops_locks", label: "Doors, stops, locks" },
  { id: "windows_latches_screens", label: "Windows, latches, screens" },
  { id: "window_coverings", label: "Window coverings" },
  { id: "closets_rods_shelves", label: "Closets, rods, shelves" },
  { id: "closet_lights_fixtures", label: "Closet lights, fixtures" },
  ...(isStateMoveInConditionForm(formKind)
    ? []
    : [{ id: "water_stains", label: waterStainLabel(formKind) }]),
  { id: "other", label: "Other" },
];

const bathItems = (
  formKind: MoveInConditionFormKind
): MoveInConditionItem[] => [
  { id: "walls", label: "Walls" },
  { id: "wallpaper", label: "Wallpaper" },
  { id: "plugs_switches_ac_vents", label: "Plugs, switches, A/C vents" },
  { id: "woodwork_baseboards", label: "Woodwork/baseboards" },
  { id: "ceiling", label: "Ceiling" },
  { id: "light_fixtures_bulbs", label: "Light fixtures, bulbs" },
  { id: "exhaust_fan_heater", label: "Exhaust fan/heater" },
  { id: "floor_carpet", label: "Floor/carpet" },
  { id: "doors_stops_locks", label: "Doors, stops, locks" },
  { id: "windows_latches_screens", label: "Windows, latches, screens" },
  { id: "window_coverings", label: "Window coverings" },
  {
    id: "sink_faucet_handles_stopper",
    label: "Sink, faucet, handles, stopper",
  },
  { id: "countertops", label: "Countertops" },
  { id: "mirror", label: "Mirror" },
  { id: "cabinets_drawers_handles", label: "Cabinets, drawers, handles" },
  { id: "toilet_paper_holder", label: "Toilet, paper holder" },
  { id: "bathtub_enclosure_stopper", label: "Bathtub, enclosure, stopper" },
  { id: "shower_doors_rods", label: "Shower, doors, rods" },
  { id: "tile", label: "Tile" },
  ...(isStateMoveInConditionForm(formKind)
    ? []
    : [
        {
          id: "plumbing_leaks_water_stains",
          label: plumbingWaterStainLabel(formKind),
        },
      ]),
  { id: "other", label: "Other" },
];

const getConditionSections = (
  formKind: MoveInConditionFormKind
): MoveInConditionSection[] => {
  const ROOM_ITEMS = roomItems(formKind);
  const BATH_ITEMS = bathItems(formKind);

  return [
    {
      id: "living_room",
      title: "Living Room",
      items: [
        ...ROOM_ITEMS.slice(0, 12),
        { id: "lamps_bulbs", label: "Lamps, bulbs" },
        ...ROOM_ITEMS.slice(12),
      ],
    },
    {
      id: "kitchen",
      title: "Kitchen",
      items: [
        ...ROOM_ITEMS.slice(0, 10),
        {
          id: "cabinets_drawers_handles",
          label: "Cabinets, drawers, handles",
        },
        { id: "countertops", label: "Countertops" },
        {
          id: "stove_oven_trays_pans_shelves",
          label: "Stove/oven, trays, pans, shelves",
        },
        { id: "vent_hood", label: "Vent hood" },
        {
          id: "refrigerator_trays_shelves",
          label: "Refrigerator, trays, shelves",
        },
        {
          id: "refrigerator_light_crisper",
          label: "Refrigerator light, crisper",
        },
        {
          id: "dishwasher_dispensers_racks",
          label: "Dishwasher, dispensers, racks",
        },
        { id: "sink_disposal", label: "Sink/disposal" },
        { id: "microwave", label: "Microwave" },
        ...(isStateMoveInConditionForm(formKind)
          ? []
          : [
              {
                id: "plumbing_leaks_water_stains",
                label: plumbingWaterStainLabel(formKind),
              },
            ]),
        { id: "other", label: "Other" },
      ],
    },
    {
      id: "general_items",
      title: "General Items",
      items: [
        { id: "thermostat", label: "Thermostat" },
        { id: "cable_tv_master_antenna", label: "Cable TV or master antenna" },
        ...(formKind === "taa"
          ? [{ id: "cable_wires", label: "Cable wires" }]
          : []),
        { id: "ac_filter", label: "A/C filter" },
        { id: "washer_dryer", label: "Washer/dryer" },
        { id: "garage_door", label: "Garage door" },
        { id: "ceiling_fans", label: "Ceiling fans" },
        {
          id: "exterior_doors_screens_doorbell",
          label: "Exterior doors, screens/screen doors, doorbell",
        },
        { id: "fireplace", label: "Fireplace" },
        { id: "other", label: "Other" },
      ],
    },
    { id: "dining_room", title: "Dining Room", items: ROOM_ITEMS },
    {
      id: "halls",
      title: "Halls",
      items: ROOM_ITEMS.filter(
        (item) =>
          !["windows_latches_screens", "window_coverings"].includes(item.id)
      ),
    },
    {
      id: "exterior",
      title: "Exterior",
      items: [
        { id: "patio_yard", label: "Patio/yard" },
        { id: "fences_gates", label: "Fences/gates" },
        { id: "faucets", label: "Faucets" },
        { id: "balconies", label: "Balconies" },
        { id: "other", label: "Other" },
      ],
    },
    {
      id: "bedroom_1",
      title: "Bedroom 1",
      descriptionLabel: "Describe which one",
      items: ROOM_ITEMS,
    },
    {
      id: "bedroom_2",
      title: "Bedroom 2",
      descriptionLabel: "Describe which one",
      items: ROOM_ITEMS,
    },
    {
      id: "bedroom_3",
      title: "Bedroom 3",
      descriptionLabel: "Describe which one",
      items: ROOM_ITEMS,
    },
    {
      id: "bath_1",
      title: "Bath 1",
      descriptionLabel: "Describe which one",
      items: BATH_ITEMS,
    },
    {
      id: "bath_2",
      title: "Bath 2",
      descriptionLabel: "Describe which one",
      items: BATH_ITEMS,
    },
    {
      id: "half_bath",
      title: "Half Bath",
      items: BATH_ITEMS.filter(
        (item) =>
          !["bathtub_enclosure_stopper", "shower_doors_rods"].includes(item.id)
      ),
    },
  ];
};

const getSafetyItems = (
  formKind: MoveInConditionFormKind
): MoveInConditionItem[] => [
  { id: "door_knob_locks", label: "Door knob locks" },
  { id: "keyed_deadbolt_locks", label: "Keyed deadbolt locks" },
  { id: "keyless_deadbolts", label: "Keyless deadbolts" },
  ...(isStateMoveInConditionForm(formKind)
    ? []
    : [{ id: "keyless_bolting_devices", label: "Keyless bolting devices" }]),
  ...(isStateMoveInConditionForm(formKind)
    ? [
        { id: "sliding_door_pin_locks", label: "Sliding door pin locks" },
        { id: "sliding_door_latches", label: "Sliding door latches" },
        {
          id: "sliding_door_security_bars",
          label: "Sliding door security bars",
        },
      ]
    : [
        { id: "sliding_door_latches", label: "Sliding door latches" },
        {
          id: "sliding_door_security_bars",
          label: "Sliding door security bars",
        },
        { id: "sliding_door_pin_locks", label: "Sliding door pin locks" },
      ]),
  { id: "doorviewers", label: "Doorviewers" },
  { id: "window_latches", label: "Window latches" },
  { id: "porch_patio_lights", label: "Porch and patio lights" },
  {
    id: "smoke_alarms",
    label:
      formKind === "taa"
        ? "Smoke alarms (push button to test)"
        : "Smoke detectors (push button to test)",
  },
  ...(formKind === "taa"
    ? [{ id: "other_detectors", label: "Other detectors" }]
    : []),
  { id: "alarm_system", label: "Alarm system" },
  { id: "fire_extinguishers", label: "Fire extinguishers" },
  { id: "garage_door_opener", label: "Garage door opener" },
  { id: "gate_access_cards", label: "Gate access card(s)" },
  ...(formKind === "nc"
    ? [
        {
          id: "carbon_monoxide_detectors",
          label: "Carbon monoxide detector(s)",
        },
      ]
    : []),
  { id: "other", label: "Other" },
];

const moveInInstructionText =
  "Within 48 hours after move-in, you must note on this form all defects, damage, or safety or pest-related concerns and return it to our representative. Otherwise, everything will be considered to be in a clean, safe, and good working condition. Please mark through items listed below or put \"none\" if the items don't exist. This form protects both you (the resident) and us (the owner). We'll use it in determining what should and should not be considered your responsibility upon move-out. You are entitled to a copy of this form after it is filled out and signed by you and us.";

const taaAcknowledgmentText = [
  "Acknowledgment. You agree you will complete and submit this form in accordance with this Lease and our Community Policies. You acknowledge you will inspect and test all the safety-related items (if in the dwelling), as well as smoke alarms and any other detector(s), and confirm that they are working, except as noted on your completed Inventory and Condition Form. All items will be assumed to be in good condition unless otherwise noted. You acknowledge you will receive written operating instructions on the alarm system and gate access entry systems (if there are any). You acknowledge that you will inspect the dwelling and confirm no signs of bed bugs or other pests are present, or that you will report any bed bug or pest issues through a work order or other repair request.",
  "In signing below, you acknowledge receipt of this form and accept the responsibility for completing it as part of the Lease Contract.",
  "You agree that, either after completion or 48 hours after move-in without returning this form (whichever comes first), it accurately reflects the condition of the premises for purposes of determining any refund due to you when you move out.",
];

const naaAcknowledgmentText = [
  "Acknowledgment. You agree you will complete and submit this form in accordance with this Lease and our Community Policies. You acknowledge you will inspect and test all safety-related items in the dwelling, including smoke alarms and any other detector(s), and confirm that they are working, except as noted on your completed Inventory and Condition Form. All items will be considered to be in good and working condition unless otherwise noted. You acknowledge you will receive written operating instructions on the alarm system and gate access entry systems (if there are any). You acknowledge that you will inspect the dwelling and confirm no signs of bed bugs or other pests are present, or, if bugs are present, that you will promptly report any bed bug or pest issues on this Inventory and Condition Form and through a written work order or other written repair request. You agree that this returned completed Inventory and Condition Form accurately reflects the condition of the dwelling for purposes of determining any refund of deposit due to you when you move out. You acknowledge that if you do not return the form within 48 hours after move-in, we will consider the dwelling to be clean, safe, free of pest or insect infestations, and in good working condition for purposes of determining any refund of deposit due to you at move-out.",
  "In signing below, you accept this inventory as part of the Lease Contract and agree that it accurately reflects the condition of the premises for purposes of determining any refund due to you when you move out.",
];

export const getMoveInConditionSchema = (
  formKind: MoveInConditionFormKind
): MoveInConditionSchema => ({
  acknowledgmentText:
    formKind === "taa" ? taaAcknowledgmentText : naaAcknowledgmentText,
  introText: moveInInstructionText,
  sections: getConditionSections(formKind),
  safetyItems: getSafetyItems(formKind),
  safetyTitle:
    formKind === "taa"
      ? "Safety or Pest-Related Items"
      : "Safety-Related Items",
  showPestConcerns: formKind === "taa",
  showSpecialProvisions: formKind !== "taa",
});

export const MOVE_IN_CONDITION_SECTIONS =
  getMoveInConditionSchema("taa").sections;

export const MOVE_IN_SAFETY_ITEMS = getMoveInConditionSchema("taa").safetyItems;
